import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { UserProfile } from "@/api/types";
import {
  clearSession,
  getStoredUser,
  persistSession,
  refreshAccessToken,
} from "@/api/client";
import { fetchMe, login as apiLogin, logout as apiLogout } from "@/api/endpoints";

interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  acceptSession: (accessToken: string, user: UserProfile) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(getStoredUser());
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await fetchMe();
      setUser(profile);
      localStorage.setItem("ai_insurance_user", JSON.stringify(profile));
    } catch {
      clearSession();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Access token lives in memory only, so every page load re-establishes
    // the session from the httpOnly refresh cookie.
    (async () => {
      try {
        const token = await refreshAccessToken();
        if (token) {
          await refreshProfile();
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshProfile]);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const session = await apiLogin(email, password, mfaCode);
    setUser(session.user);
  }, []);

  const acceptSession = useCallback((accessToken: string, profile: UserProfile) => {
    persistSession(accessToken, profile);
    setUser(profile);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // ignore logout errors
    } finally {
      clearSession();
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshProfile, acceptSession }),
    [user, loading, login, logout, refreshProfile, acceptSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
